import { defineStore, storeToRefs } from "pinia";
import api from "@/services/api";
import { useAuthStore } from "@/stores";
import type { IApiRequestLogDetail, IApiRequestLogPage } from "@/models/responses/IApiRequestLog";

export interface IApiRequestLogFilter {
  method?: string | null;
  path?: string | null;
  statusCode?: number | null;
  minStatusCode?: number | null;
  maxStatusCode?: number | null;
  userId?: number | null;
  from?: string | null;
  to?: string | null;
  minResponseTimeMs?: number | null;
  skip: number;
  take: number;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function buildQuery(filter: IApiRequestLogFilter): string {
  const params = new URLSearchParams();
  if (filter.method) params.append("method", filter.method);
  if (filter.path) params.append("path", filter.path);
  if (isFiniteNumber(filter.statusCode)) params.append("statusCode", String(filter.statusCode));
  if (isFiniteNumber(filter.minStatusCode)) params.append("minStatusCode", String(filter.minStatusCode));
  if (isFiniteNumber(filter.maxStatusCode)) params.append("maxStatusCode", String(filter.maxStatusCode));
  if (isFiniteNumber(filter.userId)) params.append("userId", String(filter.userId));
  if (filter.from) params.append("from", filter.from);
  if (filter.to) params.append("to", filter.to);
  if (isFiniteNumber(filter.minResponseTimeMs)) params.append("minResponseTimeMs", String(filter.minResponseTimeMs));
  params.append("skip", String(filter.skip));
  params.append("take", String(filter.take));
  return params.toString();
}

export const useApiRequestLogsStore = defineStore("api-request-logs", {
  state: () => ({
    error: "" as any,
    loading: false as boolean,
    page: { total: 0, items: [] } as IApiRequestLogPage,
    detail: null as IApiRequestLogDetail | null,
    detailLoading: false as boolean,
  }),
  actions: {
    async search(filter: IApiRequestLogFilter) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      this.loading = true;
      try {
        const response = await api.get<IApiRequestLogPage>(`v1.0/api-request-logs?${buildQuery(filter)}`);
        this.page = response.data;
      } catch (error) {
        this.error = error;
      } finally {
        this.loading = false;
      }
    },
    async loadDetail(id: number) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      this.detail = null;
      this.detailLoading = true;
      try {
        const response = await api.get<IApiRequestLogDetail>(`v1.0/api-request-logs/${id}`);
        this.detail = response.data;
      } catch (error) {
        this.error = error;
      } finally {
        this.detailLoading = false;
      }
    },
    clearDetail() {
      this.detail = null;
    },
  },
});
